import { ChevronRightIcon, HomeIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

const Breadcrumbs: React.FC = () => {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter(Boolean);

  // Generate breadcrumb items based on current path
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const items: BreadcrumbItem[] = [{ label: 'Dashboard', href: '/dashboard' }];

    if (pathnames.length === 0 || pathnames[0] === 'dashboard') {
      return [{ label: 'Dashboard' }];
    }

    for (let i = 0; i < pathnames.length; i++) {
      const path = pathnames[i];
      const href = '/' + pathnames.slice(0, i + 1).join('/');
      const isLast = i === pathnames.length - 1;

      let label = '';
      switch (path) {
        case 'customers':
          label = 'Customers';
          break;
        case 'vehicles':
          label = 'Vehicles';
          break;
        case 'deals':
          label = 'Deals';
          break;
        case 'users':
          label = 'Users';
          break;
        case 'tracker':
          label = 'SuperTracker';
          break;
        case 'settings':
          label = 'Settings';
          break;
        default:
          // For detail pages (UUIDs), show a generic label
          if (path.match(/^[0-9a-f-]{36}$/i)) {
            const previousPath = pathnames[i - 1];
            switch (previousPath) {
              case 'customers':
                label = 'Customer Details';
                break;
              case 'vehicles':
                label = 'Vehicle Details';
                break;
              case 'deals':
                label = 'Deal Details';
                break;
              default:
                label = 'Details';
            }
          } else {
            label = path.charAt(0).toUpperCase() + path.slice(1);
          }
      }

      items.push({
        label,
        href: isLast ? undefined : href,
      });
    }

    return items;
  };

  const breadcrumbs = generateBreadcrumbs();

  // Don't show breadcrumbs if we're just on the dashboard
  if (breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <nav className="flex mb-6" aria-label="Breadcrumb">
      <ol className="inline-flex items-center space-x-1 md:space-x-3">
        <li className="inline-flex items-center">
          <Link
            to="/dashboard"
            className="inline-flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
          >
            <HomeIcon className="w-4 h-4 mr-2" />
            Dashboard
          </Link>
        </li>
        {breadcrumbs.slice(1).map((item, index) => (
          <li key={index}>
            <div className="flex items-center">
              <ChevronRightIcon className="w-4 h-4 text-gray-400 dark:text-gray-500" />
              {item.href ? (
                <Link
                  to={item.href}
                  className="ml-1 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 md:ml-2"
                >
                  {item.label}
                </Link>
              ) : (
                <span className="ml-1 text-sm font-medium text-gray-500 dark:text-gray-400 md:ml-2">
                  {item.label}
                </span>
              )}
            </div>
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
